import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useLocation } from "react-use";
import { t } from "ttag";

import { skipToken, useGetDatabaseQuery } from "metabase/api";
import { useDispatch } from "metabase/redux";
import { Button, Flex, Icon, Loader, Menu, Text } from "metabase/ui";
import {
  useGetGsheetsFolderQuery,
  useSyncGsheetsFolderMutation,
} from "metabase-enterprise/api";
import { EnterpriseApi } from "metabase-enterprise/api/api";

import { GdriveConnectionModal } from "./GdriveConnectionModal";
import { GdriveErrorMenuItem } from "./GdriveErrorMenuItem";
import { trackSheetConnectionClick } from "./analytics";
import { SYNC_POLL_INTERVAL } from "./constants";
import { getStatus, useShowGdrive } from "./utils";

export function GdriveDbMenu() {
  const [showModal, setShowModal] = useState(false);

  const url = useLocation();
  const databaseId = /databases\/(\d+)/.exec(url.pathname ?? "")?.[1];

  const { data: databaseInfo } = useGetDatabaseQuery(
    databaseId ? { id: Number(databaseId) } : skipToken,
  );

  const isDwh = databaseInfo?.is_attached_dwh;
  const showGdrive = useShowGdrive();

  const showMenu = showGdrive && isDwh;

  const { data: folderInfo, error: folderError } = useGetGsheetsFolderQuery(
    showMenu ? undefined : skipToken,
  );

  if (!showMenu) {
    return null;
  }

  const status = getStatus({ status: folderInfo?.status, error: folderError });

  if (status === "not-connected") {
    return (
      <>
        <Button
          variant="subtle"
          onClick={() => {
            setShowModal(true);
            trackSheetConnectionClick({ from: "db-page" });
          }}
          leftSection={<Icon name="google_sheet" />}
        >
          {t`Connect Google Sheets`}
        </Button>
        <GdriveConnectionModal
          isModalOpen={showModal}
          onClose={() => setShowModal(false)}
          reconnect={false}
        />
      </>
    );
  }

  return (
    <>
      <Menu>
        <Menu.Target>
          <Button
            variant="subtle"
            leftSection={<Icon name="google_sheet" />}
            rightSection={<Icon name="chevrondown" />}
          >
            {t`Google Sheets`}
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <SyncNowButton
            disabled={status === "syncing" || status === "initializing"}
          />
          <Menu.Item
            leftSection={<Icon name="close" />}
            fw="bold"
            onClick={() => setShowModal(true)}
          >
            {t`Disconnect`}
          </Menu.Item>
          <Menu.Divider />
          <Menu.Label>
            <MenuSyncStatus />
          </Menu.Label>
        </Menu.Dropdown>
      </Menu>
      <GdriveConnectionModal
        isModalOpen={showModal}
        onClose={() => setShowModal(false)}
        reconnect={false}
      />
    </>
  );
}

function SyncNowButton({ disabled }: { disabled: boolean }) {
  const [doSync, { isLoading }] = useSyncGsheetsFolderMutation();

  return (
    <Menu.Item
      disabled={disabled || isLoading}
      leftSection={<Icon name="sync" />}
      fw="bold"
      onClickCapture={async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        await doSync();
      }}
    >
      {t`Sync`}
    </Menu.Item>
  );
}

function MenuSyncStatus() {
  const dispatch = useDispatch();
  const folderQuery = useGetGsheetsFolderQuery(undefined, {
    refetchOnMountOrArgChange: 5,
  });
  const { data: folderInfo, error: folderError } = folderQuery;

  const folderStatus = getStatus({
    status: folderInfo?.status,
    error: folderError,
  });

  const isInProgress =
    folderStatus === "syncing" || folderStatus === "initializing";

  useEffect(() => {
    if (isInProgress) {
      const timeout = setTimeout(() => {
        dispatch(EnterpriseApi.util.invalidateTags(["gsheets-status"]));
      }, SYNC_POLL_INTERVAL);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [folderQuery, isInProgress, dispatch]); // need folderQuery so this runs on every refetch

  const lastSync = folderInfo?.last_sync_at;
  const nextSync = folderInfo?.next_sync_at;

  const lastSyncRelative = lastSync ? dayjs.unix(lastSync).fromNow() : null;

  const nextSyncOverDue =
    !lastSync || !nextSync || dayjs.unix(nextSync).isBefore(dayjs());

  const nextSyncRelative = !nextSyncOverDue
    ? dayjs.unix(nextSync).fromNow()
    : t`soon` + "™";

  if (folderStatus === "error") {
    // transient error, will be automatically retried
    if (!folderError && lastSync) {
      const isDelayed = dayjs
        .unix(lastSync)
        .isBefore(dayjs().subtract(1, "hour"));
      return (
        <GdriveRecoveringMenuItem
          lastSyncRelative={lastSyncRelative ?? ""}
          isDelayed={isDelayed}
        />
      );
    }
    return <GdriveErrorMenuItem error={folderError ?? folderInfo} />;
  }

  return (
    <>
      {folderStatus === "syncing" || folderStatus === "initializing" ? (
        <SyncingText />
      ) : (
        <Text fw="bold">{t`Next sync ${nextSyncRelative}`}</Text>
      )}
      {lastSyncRelative && (
        <Text fz="sm">{t`Last synced ${lastSyncRelative}`}</Text>
      )}
    </>
  );
}

const SyncingText = () => (
  <Flex justify="space-between" align="center">
    <Text fw="bold">{t`Syncing`}</Text>
    <Loader size="xs" />
  </Flex>
);

function GdriveRecoveringMenuItem({
  lastSyncRelative,
  isDelayed,
}: {
  lastSyncRelative: string;
  isDelayed: boolean;
}) {
  if (isDelayed) {
    return (
      <>
        <Text fw="bold">{t`Sync delayed`}</Text>
        <Text fz="sm">{t`Last synced ${lastSyncRelative}. Your data may be a bit behind. We're retrying the sync.`}</Text>
      </>
    );
  }

  return (
    <>
      <Text fw="bold">{t`Retrying shortly`}</Text>
      <Text fz="sm">{t`Last synced ${lastSyncRelative}. Your data is up to date as of the last sync.`}</Text>
    </>
  );
}
